import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import axios from "axios";
import { 
  Droplets, 
  Wrench, 
  AlertTriangle, 
  Bug, 
  Smartphone, 
  Eye,
  ArrowLeft,
  Send,
  User,
  Building,
  MapPin,
  Mail,
  Phone,
  Save,
  Loader2,
  CheckCircle2,
  Activity,
  Package,
  Shield,
  Beaker,
  FolderKanban,
  Truck,
  Users,
  Settings,
  Brain,
  ShoppingCart,
  Zap,
  ThermometerSun,
  Filter,
  Sun,
  FlaskConical,
  Gauge,
  CircuitBoard,
  Pipette,
  Camera,
  ImagePlus,
  Trash2,
  Info,
  Calendar,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Profile Storage Hook
const useProfile = () => {
  const [profile, setProfile] = useState({
    hospital_name: "",
    address: "",
    contact_name: "",
    contact_email: "",
    contact_phone: ""
  });

  useEffect(() => {
    const saved = localStorage.getItem("sterilemate_profile");
    if (saved) {
      try {
        setProfile(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load profile", e);
      }
    }
  }, []);

  const saveProfile = (newProfile) => {
    setProfile(newProfile);
    localStorage.setItem("sterilemate_profile", JSON.stringify(newProfile));
    toast.success("Profile saved successfully!");
  };

  return { profile, setProfile, saveProfile };
};

// Triage Options - Expanded list with all maintenance categories and color-coded icons
const triageOptions = [
  { 
    value: "water_quality", 
    label: "Water Quality Issue", 
    icon: Droplets, 
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    borderColor: "border-blue-200",
    selectedBg: "bg-blue-50",
    description: "Contamination, pH imbalance, conductivity issues"
  },
  { 
    value: "equipment_malfunction", 
    label: "Equipment Malfunction", 
    icon: Wrench, 
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    borderColor: "border-amber-200",
    selectedBg: "bg-amber-50",
    description: "General equipment not working properly"
  },
  { 
    value: "emergency", 
    label: "Emergency/Urgent", 
    icon: AlertTriangle, 
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    borderColor: "border-red-200",
    selectedBg: "bg-red-50",
    description: "Critical failure, patient safety concern"
  },
  { 
    value: "biological_contamination", 
    label: "Biological Contamination", 
    icon: Bug, 
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    borderColor: "border-green-200",
    selectedBg: "bg-green-50",
    description: "Bacteria, biofilm, microbial growth"
  },
  { 
    value: "electrical", 
    label: "Electrical Issue", 
    icon: Zap, 
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
    borderColor: "border-yellow-200",
    selectedBg: "bg-yellow-50",
    description: "Power, wiring, electrical component failure"
  },
  { 
    value: "plumbing", 
    label: "Plumbing Issue", 
    icon: Pipette, 
    iconBg: "bg-cyan-100",
    iconColor: "text-cyan-600",
    borderColor: "border-cyan-200",
    selectedBg: "bg-cyan-50",
    description: "Pipes, connections, water flow problems"
  },
  { 
    value: "water_leakage", 
    label: "Water Leakage", 
    icon: Droplets, 
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
    borderColor: "border-sky-200",
    selectedBg: "bg-sky-50",
    description: "Leaks, drips, water damage concerns"
  },
  { 
    value: "damaged_pipe", 
    label: "Damaged Pipe", 
    icon: AlertTriangle, 
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    borderColor: "border-orange-200",
    selectedBg: "bg-orange-50",
    description: "Cracked, broken, or corroded pipes"
  },
  { 
    value: "ro_system", 
    label: "Reverse Osmosis System", 
    icon: Filter, 
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    borderColor: "border-indigo-200",
    selectedBg: "bg-indigo-50",
    description: "RO system maintenance, repair, troubleshooting"
  },
  { 
    value: "filter_replacement", 
    label: "Filter Replacement", 
    icon: Filter, 
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    borderColor: "border-teal-200",
    selectedBg: "bg-teal-50",
    description: "Scheduled or needed filter changes"
  },
  { 
    value: "uv_light_failure", 
    label: "UV Light Failure/Replacement", 
    icon: Sun, 
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    borderColor: "border-violet-200",
    selectedBg: "bg-violet-50",
    description: "UV sterilization lamp issues or replacement"
  },
  { 
    value: "salt_replenishing", 
    label: "Salt Replenishing", 
    icon: FlaskConical, 
    iconBg: "bg-slate-100",
    iconColor: "text-slate-600",
    borderColor: "border-slate-200",
    selectedBg: "bg-slate-50",
    description: "Water softener salt refill needed"
  },
  { 
    value: "resistivity_meter", 
    label: "Resistivity Meter Failure", 
    icon: Gauge, 
    iconBg: "bg-rose-100",
    iconColor: "text-rose-600",
    borderColor: "border-rose-200",
    selectedBg: "bg-rose-50",
    description: "Resistivity meter repair or replacement"
  },
  { 
    value: "conductivity_meter", 
    label: "Conductivity Meter Issue", 
    icon: CircuitBoard, 
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    borderColor: "border-emerald-200",
    selectedBg: "bg-emerald-50",
    description: "Conductivity meter replace or troubleshoot"
  },
  { 
    value: "calibration", 
    label: "Calibration/Testing", 
    icon: Smartphone, 
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    borderColor: "border-purple-200",
    selectedBg: "bg-purple-50",
    description: "Sensor calibration, routine testing"
  },
  { 
    value: "inspection", 
    label: "Inspection Request", 
    icon: Eye, 
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    borderColor: "border-blue-200",
    selectedBg: "bg-blue-50",
    description: "Scheduled or ad-hoc inspection"
  },
  { 
    value: "distribution_pump", 
    label: "Distribution Pump", 
    icon: Activity, 
    iconBg: "bg-fuchsia-100",
    iconColor: "text-fuchsia-600",
    borderColor: "border-fuchsia-200",
    selectedBg: "bg-fuchsia-50",
    description: "Pump repair, replacement, or troubleshooting"
  },
  { 
    value: "reserve_tank", 
    label: "Reserve Tank", 
    icon: Package, 
    iconBg: "bg-lime-100",
    iconColor: "text-lime-600",
    borderColor: "border-lime-200",
    selectedBg: "bg-lime-50",
    description: "Storage tank inspection, repair, or cleaning"
  },
  { 
    value: "water_softener", 
    label: "Water Softener", 
    icon: Droplets, 
    iconBg: "bg-pink-100",
    iconColor: "text-pink-600",
    borderColor: "border-pink-200",
    selectedBg: "bg-pink-50",
    description: "Softener system repair or maintenance"
  },
  { 
    value: "carbon_filter", 
    label: "Carbon Filter", 
    icon: Filter, 
    iconBg: "bg-stone-100",
    iconColor: "text-stone-600",
    borderColor: "border-stone-200",
    selectedBg: "bg-stone-50",
    description: "Carbon filter replacement or inspection"
  }
];

export default function MaintenancePage() {
  const { profile, setProfile, saveProfile } = useProfile();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [formData, setFormData] = useState({
    issue_type: "",
    description: "",
    priority: "normal",
    asset_id: "",
    location_details: ""
  });
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch facility assets when profile loads
  useEffect(() => {
    const fetchAssets = async () => {
      if (profile.hospital_id) {
        try {
          const res = await axios.get(`${API}/assets/facility/${profile.hospital_id}`);
          setAssets(res.data.assets || []);
        } catch (err) {
          console.log("No assets found for facility");
        }
      }
    };
    fetchAssets();
  }, [profile.hospital_id]);

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 5 photos total
    if (photos.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    setPhotoUploading(true);
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a valid image or video`);
        continue;
      }
      
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 50MB.`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/maintenance/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setPhotos(prev => [...prev, { url: res.data.url, name: file.name, type: file.type }]);
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        console.error(err);
      }
    }
    
    setPhotoUploading(false);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Handle asset selection
  const handleAssetSelect = (assetId) => {
    const actualId = assetId === "none" ? "" : assetId;
    setFormData({ ...formData, asset_id: actualId });
    const asset = assets.find(a => a.id === actualId);
    setSelectedAsset(asset || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.hospital_name || !profile.contact_email) {
      toast.error("Please save your facility profile first");
      setShowProfileModal(true);
      return;
    }

    if (!formData.issue_type) {
      toast.error("Please select an issue type");
      return;
    }

    // Require at least one photo
    if (photos.length === 0) {
      toast.error("At least one photo is required to submit a maintenance request");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        facility_name: profile.hospital_name,
        facility_id: profile.hospital_id || null,
        address: profile.address,
        contact_name: profile.contact_name,
        contact_email: profile.contact_email,
        contact_phone: profile.contact_phone,
        issue_type: formData.issue_type,
        description: formData.description,
        priority: formData.priority,
        asset_id: formData.asset_id || null,
        location_details: formData.location_details,
        photo_urls: photos.map(p => p.url),
        video_urls: []
      };

      const res = await axios.post(`${API}/maintenance-requests`, payload);
      
      // Show warranty info if applicable
      if (res.data.is_warranty) {
        toast.success("Request submitted! Note: This equipment is under warranty.");
      } else {
        toast.success("Maintenance request submitted successfully!");
      }
      
      setSubmitted(true);
      setFormData({
        issue_type: "",
        description: "",
        priority: "normal",
        asset_id: "",
        location_details: ""
      });
      setPhotos([]);
      setSelectedAsset(null);
    } catch (error) {
      console.error("Submission error:", error);
      const errMsg = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-manrope">Request Submitted!</h2>
            <p className="text-slate-500 mb-6">
              We&apos;ve received your maintenance request and will respond shortly.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => setSubmitted(false)} className="bg-blue-700 hover:bg-blue-800 text-white">
                Submit Another Request
              </Button>
              <Link to="/">
                <Button variant="outline" className="w-full border-slate-200 text-slate-600 hover:bg-slate-50">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 md:px-6 py-4 max-w-5xl mx-auto">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/20d8ab9a-0d9d-4358-be02-42034c0d9833/images/13cb943be22c297ac1acf970329b9be0ac6336421b2a990a33401ce5f6aa5aec.png"
                  alt="AskIsaac"
                  className="w-10 h-10 rounded-lg shadow-md"
                />
                <div className="hidden sm:block">
                  <h1 className="text-slate-900 font-bold text-lg tracking-tight font-manrope">AskIsaac</h1>
                  <p className="text-slate-400 text-xs"></p>
                </div>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight font-manrope sm:hidden">Maintenance</h1>
              <img 
                src="https://customer-assets.emergentagent.com/job_projectmate-6/artifacts/1cyzft3x_Sterile%20Mate%20Logo%20%282%29.jpg"
                alt="Sterile Mate"
                className="h-10 w-auto object-contain hidden sm:block"
              />
              <Button variant="outline" onClick={() => setShowProfileModal(true)} className="hidden sm:flex border-slate-200 text-slate-600 hover:bg-slate-50">
                <User className="w-4 h-4 mr-2" /> My Profile
              </Button>
              <Button variant="outline" onClick={() => setShowProfileModal(true)} className="sm:hidden border-slate-200 text-slate-600 hover:bg-slate-50 p-2">
                <User className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Title - Desktop */}
        <div className="bg-white border-b border-slate-200 hidden sm:block">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight font-manrope">Maintenance Request</h1>
            <p className="text-sm text-slate-500">Report equipment issues or request service</p>
          </div>
        </div>

        {/* Main Content */}
        <main className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto">
          {/* Profile Summary */}
          {profile.hospital_name && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{profile.hospital_name}</p>
                    <p className="text-sm text-slate-500">{profile.contact_name} • {profile.contact_email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowProfileModal(true)} className="text-slate-500 hover:text-blue-700 hover:bg-blue-50">
                  Edit
                </Button>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Issue Type Selection */}
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 font-manrope">What type of issue are you experiencing?</h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                {triageOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = formData.issue_type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, issue_type: option.value })}
                      data-testid={`issue-type-${option.value}`}
                      className={`
                        p-2.5 sm:p-3 rounded-xl border-2 text-left transition-all duration-200
                        ${isSelected 
                          ? `${option.borderColor} ${option.selectedBg} shadow-md` 
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        }
                      `}
                    >
                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-3">
                        <div className={`w-10 h-10 sm:w-9 sm:h-9 ${option.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 sm:w-[18px] sm:h-[18px] ${option.iconColor}`} strokeWidth={1.5} />
                        </div>
                        <div className="min-w-0 flex-1 text-center sm:text-left">
                          <span className={`font-medium text-xs sm:text-sm block ${isSelected ? option.iconColor : "text-slate-700"}`}>
                            {option.label}
                          </span>
                          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-2 hidden sm:block">{option.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Details */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 font-manrope">Issue Details</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700">Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please describe the issue in detail..."
                    rows={4}
                    required
                    className="mt-1 border-slate-200"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Response Tier</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {/* Standard */}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: "normal" })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          formData.priority === "normal" || formData.priority === "low"
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="tier-standard"
                      >
                        <Clock className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                        <p className="font-semibold text-sm text-slate-900">Standard</p>
                        <p className="text-xs font-bold text-blue-600">24-48 hrs</p>
                      </button>
                      
                      {/* Priority */}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: "high" })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          formData.priority === "high"
                            ? "border-amber-500 bg-amber-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="tier-priority"
                      >
                        <Zap className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                        <p className="font-semibold text-sm text-slate-900">Priority</p>
                        <p className="text-xs font-bold text-amber-600">4-8 hrs</p>
                      </button>
                      
                      {/* STAT */}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: "urgent" })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          formData.priority === "urgent"
                            ? "border-red-500 bg-red-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="tier-stat"
                      >
                        <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
                        <p className="font-semibold text-sm text-slate-900">STAT</p>
                        <p className="text-xs font-bold text-red-600">2 hrs</p>
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700">Location/Room</Label>
                    <Input
                      value={formData.location_details}
                      onChange={(e) => setFormData({ ...formData, location_details: e.target.value })}
                      placeholder="e.g., Building A, Floor 2, Room 205"
                      className="mt-1 border-slate-200"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Asset Selection */}
            {assets.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-manrope">Select Equipment (Optional)</h3>
                <p className="text-sm text-slate-500 mb-4">Link this request to a specific piece of equipment for faster service</p>
                <Select 
                  value={formData.asset_id} 
                  onValueChange={handleAssetSelect}
                >
                  <SelectTrigger className="border-slate-200">
                    <SelectValue placeholder="Select equipment..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific equipment</SelectItem>
                    {assets.map((asset) => (
                      <SelectItem key={asset.id} value={asset.id}>
                        {asset.name} {asset.serial_number ? `(S/N: ${asset.serial_number})` : ''} - {asset.location || 'Location N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Show asset details if selected */}
                {selectedAsset && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Settings className="w-5 h-5 text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{selectedAsset.name}</h4>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                          {selectedAsset.serial_number && (
                            <p className="text-slate-600"><span className="text-slate-500">S/N:</span> {selectedAsset.serial_number}</p>
                          )}
                          {selectedAsset.location && (
                            <p className="text-slate-600"><span className="text-slate-500">Location:</span> {selectedAsset.location}</p>
                          )}
                          {selectedAsset.install_date && (
                            <p className="text-slate-600 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Installed: {new Date(selectedAsset.install_date).toLocaleDateString()}
                            </p>
                          )}
                          {selectedAsset.last_service_date && (
                            <p className="text-slate-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Last Service: {new Date(selectedAsset.last_service_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {selectedAsset.warranty_expiry && new Date(selectedAsset.warranty_expiry) > new Date() && (
                          <div className="mt-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs inline-flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            Under Warranty until {new Date(selectedAsset.warranty_expiry).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Photo Upload Section - REQUIRED */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-900 font-manrope">Photos / Evidence *</h3>
                <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">Required</span>
              </div>
              <p className="text-sm text-slate-500 mb-4">Upload at least one photo showing the issue. This helps us dispatch the right technician.</p>
              
              {/* Photo Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                    {photo.type.startsWith('image/') ? (
                      <img src={photo.url} alt={photo.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-800">
                        <span className="text-white text-xs">Video</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Upload Button */}
                {photos.length < 5 && (
                  <label className={`
                    aspect-square rounded-lg border-2 border-dashed border-slate-300 
                    flex flex-col items-center justify-center cursor-pointer
                    hover:border-blue-500 hover:bg-blue-50 transition-colors
                    ${photoUploading ? 'pointer-events-none opacity-50' : ''}
                  `}>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={photoUploading}
                    />
                    {photoUploading ? (
                      <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-6 h-6 text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">Add Photo</span>
                      </>
                    )}
                  </label>
                )}
              </div>
              
              {photos.length === 0 && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <Info className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">Please upload at least one photo of the issue before submitting.</p>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between">
              <Link to="/">
                <Button type="button" variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
              <Button 
                type="submit" 
                disabled={isSubmitting || !formData.issue_type || photos.length === 0}
                className="bg-blue-700 hover:bg-blue-800 text-white shadow-sm"
                data-testid="submit-maintenance-request"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </main>
      </div>

      {/* Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Facility Profile</DialogTitle>
            <DialogDescription className="text-slate-500">
              Save your facility information for faster requests
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Hospital/Facility Name *</Label>
              <Input
                value={profile.hospital_name}
                onChange={(e) => setProfile({ ...profile, hospital_name: e.target.value })}
                placeholder="Memorial Hospital"
                className="mt-1 border-slate-200"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                placeholder="123 Medical Center Dr"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Name *</Label>
              <Input
                value={profile.contact_name}
                onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={profile.contact_email}
                onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                placeholder="john@hospital.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={profile.contact_phone}
                onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                placeholder="(800) 988-3892"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowProfileModal(false)}>Cancel</Button>
            <Button onClick={() => { saveProfile(profile); setShowProfileModal(false); }}>
              <Save className="w-4 h-4 mr-2" /> Save Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
